import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, orderBy, Timestamp, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { InviteCode } from '@/types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Gift, Plus, Copy, Check, X } from 'lucide-react';
import { format } from 'date-fns';

export const AdminInvites = () => {
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [customCode, setCustomCode] = useState('');

  useEffect(() => {
    fetchInviteCodes();
  }, []);

  const fetchInviteCodes = async () => {
    try {
      const invitesRef = collection(db, 'inviteCodes');
      const q = query(invitesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const codes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as InviteCode[];
      
      setInviteCodes(codes);
    } catch (error) {
      console.error('Failed to fetch invite codes:', error);
      alert('招待コードの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  const handleOpenModal = () => {
    setCustomCode('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCustomCode('');
  };

  const checkCodeExists = async (code: string): Promise<boolean> => {
    try {
      const invitesRef = collection(db, 'inviteCodes');
      const q = query(invitesRef, where('code', '==', code));
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Failed to check code:', error);
      return false;
    }
  };

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customCode.trim()) {
      alert('招待コードを入力してください。');
      return;
    }

    // 英数字のみ許可
    if (!/^[a-zA-Z0-9]+$/.test(customCode)) {
      alert('招待コードは英数字のみ使用できます。');
      return;
    }

    setGenerating(true);
    
    try {
      // 重複チェック
      const exists = await checkCodeExists(customCode.toUpperCase());
      if (exists) {
        alert('このコードは既に使用されています。別のコードを入力してください。');
        setGenerating(false);
        return;
      }

      await addDoc(collection(db, 'inviteCodes'), {
        code: customCode.toUpperCase(),
        status: 'active',
        createdAt: Timestamp.now(),
      });
      
      alert(`招待コードを生成しました: ${customCode.toUpperCase()}`);
      handleCloseModal();
      fetchInviteCodes();
    } catch (error) {
      console.error('Failed to generate invite code:', error);
      alert('招待コードの生成に失敗しました。');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateRandom = () => {
    const randomCode = generateRandomCode();
    setCustomCode(randomCode);
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
      alert('コードのコピーに失敗しました。');
    }
  };

  const stats = {
    total: inviteCodes.length,
    used: inviteCodes.filter((code) => code.status === 'used').length,
    active: inviteCodes.filter((code) => code.status === 'active').length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ページヘッダー */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">招待コード管理</h1>
            <p className="text-gray-600 mt-1">招待コードの生成と使用状況確認</p>
          </div>
          
          <button
            onClick={handleOpenModal}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            新規生成
          </button>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Gift className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">総発行数</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Gift className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">使用済み</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.used}</p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Gift className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-600">未使用</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
          </div>
        </div>

        {/* 招待コードリスト */}
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">招待コード一覧</h2>
          
          {inviteCodes.length === 0 ? (
            <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <Gift className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">招待コードはまだありません</p>
                <p className="text-sm text-gray-400 mt-1">
                  「新規生成」ボタンから招待コードを作成できます
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">招待コード</th>
                    <th scope="col" className="px-6 py-3">ステータス</th>
                    <th scope="col" className="px-6 py-3">生成日時</th>
                    <th scope="col" className="px-6 py-3">使用日時</th>
                    <th scope="col" className="px-6 py-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {inviteCodes.map((inviteCode) => (
                    <tr key={inviteCode.id} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded font-bold">
                          {inviteCode.code}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            inviteCode.status === 'used'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {inviteCode.status === 'used' ? '使用済み' : '未使用'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(inviteCode.createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {inviteCode.usedAt
                          ? format(inviteCode.usedAt.toDate(), 'yyyy/MM/dd HH:mm')
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {inviteCode.status === 'active' && (
                          <button
                            onClick={() => handleCopyCode(inviteCode.code)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                          >
                            {copiedCode === inviteCode.code ? (
                              <>
                                <Check className="w-3 h-3" />
                                コピー済み
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                コピー
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* モーダル */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">招待コードを生成</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateCode} className="space-y-4">
                <div>
                  <label htmlFor="customCode" className="block text-sm font-medium text-gray-700 mb-1">
                    招待コード
                  </label>
                  <input
                    type="text"
                    id="customCode"
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                    required
                    className="input w-full font-mono"
                    placeholder="例: Welcome2026Spring"
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    英数字のみ使用できます（自動的に大文字に変換されます）
                  </p>
                </div>
                
                <button
                  type="button"
                  onClick={handleGenerateRandom}
                  className="btn-secondary w-full text-sm"
                >
                  ランダム生成（8文字）
                </button>
                
                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={generating}
                    className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? '生成中...' : '生成する'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-secondary flex-1"
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
